import { Box } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { DataGridErrorOverlay } from "../../common-ui/DataGridErrorOverlay";

import { PageWrapper, PageWrapperProps } from "../../common-ui/PageWrapper";
import {
  ProgramNetworkErrors,
  ProgramNotifications,
  alphabetizePrograms,
  useAllPrograms
} from "./programUtils";
import { useTrainingStages } from "../TrainingStages/trainingStagesAPI";

// Shape returned by the programs-list REST endpoint (not the Amplify Program type).
type ProgramListItem = {
  id: string;
  name: string | null;
  description: string | null;
  institution: { id: string; name: string | null } | null;
  trainingStageId: string | null;
  networks: { id: string; name: string }[];
};

const pageDetails: PageWrapperProps = {
  className: "Programs",
  headerText: "Programs"
};

export const Programs = ({
  disableVirtualization
}: {
  disableVirtualization?: boolean;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { programs: rawPrograms, status } = useAllPrograms();
  // useAllPrograms is typed with the Amplify Program label; the runtime data is
  // the REST shape above.
  const programs = rawPrograms as unknown as ProgramListItem[] | null;
  const { trainingStages } = useTrainingStages();

  // Training-stage options for the filter dropdown, ordered by sortOrder.
  const stageOptions = [...trainingStages]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((stage) => stage.displayName);

  // Here, they want the programs organized first by Institution name, then by Program name.
  // This is slightly different from the standard alphabetizePrograms features, which just
  // organizes them by Program name.
  let alphabetizedPrograms: ProgramListItem[] = [];
  if (programs) {
    alphabetizedPrograms = alphabetizePrograms(programs, true);
  }

  const columns: GridColDef[] = [
    {
      headerName: t("Name"),
      field: "name",
      flex: 1
    },
    {
      headerName: t("Description"),
      field: "description",
      flex: 1
    },
    {
      headerName: t("Institution"),
      field: "institutionName",
      valueGetter: (params) => params.row.institution.name,
      flex: 1
    },
    {
      headerName: t("Network"),
      field: "network",
      // programs-list REST endpoint returns networks as [{ id, name }].
      valueGetter: (params) =>
        (params.row.networks ?? [])
          .map((n) => n.name)
          .filter(Boolean)
          .join(", "),
      flex: 1
    },
    {
      headerName: t("Training Stage"),
      field: "trainingStage",
      type: "singleSelect",
      valueOptions: stageOptions,
      valueGetter: (params) =>
        trainingStages.find(
          (stage) => stage.id === (params.row as any).trainingStageId
        )?.displayName,
      flex: 1
    },
    {
      headerName: t("ID"),
      field: "id",
      flex: 1
    }
  ];

  return (
    <PageWrapper {...pageDetails}>
      <div>
        <button onClick={() => navigate(`/programs/new`)}>
          {t("New Program")}
        </button>
      </div>
      <DataGrid
        columns={columns}
        rows={alphabetizedPrograms || []}
        aria-label="programs-table"
        disableVirtualization={disableVirtualization}
        loading={status === "loading"}
        localeText={{
          noRowsLabel: t(ProgramNotifications.NoPrograms)
        }}
        onRowClick={(rowData) => {
          const { row } = rowData;
          const { id } = row;
          if (id) {
            navigate(`${id}`);
          }
        }}
      />
    </PageWrapper>
  );
};
